import type { VisitType } from '@lmc/ts-common'
import {
  Add,
  EmptyState,
  FormCard,
  FormItem,
  Input,
  NoDataIcon,
  Select,
  SelectAddOption,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { Select as AntdSelect, Divider, Form, InputNumber, Tooltip } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { DeleteButton } from 'screens/Rostering/components'
import { InputSuffix } from 'screens/Rostering/formComponents'
import {
  getFilteredFundingTypeOptions,
  getRequiredRule
} from 'screens/Rostering/utils'
import styled from 'styled-components'
import { commonPatterns } from 'utils'

type VisitTypeValue = { id?: string; name: string }

type FundingFormItemProps = {
  onDelete: () => void
  name: number
  fundingTypeOptions: DefaultOptionType[]
  selectedResident: string | undefined
  showChargeRates?: boolean
  visitTypes?: VisitType[]
  isAdmin?: boolean
}

export const FundingFormItem = ({
  onDelete,
  name,
  fundingTypeOptions,
  selectedResident,
  showChargeRates = false,
  visitTypes = [],
  isAdmin = false
}: FundingFormItemProps) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const [isAddingRate, setIsAddingRate] = useState(false)

  const getFundingSourceTitle = (id: string) => {
    const fundingOption = fundingTypeOptions.find(
      (fundingOption) => fundingOption.value === id
    )

    return fundingOption?.label
  }

  const fundingUrl = selectedResident
    ? `/residents/${selectedResident}/care-package-and-patterns`
    : '/residents'

  // Test ID constants following best practices
  const baseTestId = 'funding-form-item'
  const fundingTypeDropdownTestId = commonPatterns.input(
    baseTestId,
    'funding-type'
  )
  const fundedHoursInputTestId = commonPatterns.input(
    baseTestId,
    'funded-hours'
  )
  const deleteButtonTestId = commonPatterns.button(baseTestId, 'delete')
  const formCardTestId = `${baseTestId}-form-card`
  const emptyStateTestId = `${baseTestId}-empty-state`
  const editButtonTestId = commonPatterns.button(baseTestId, 'edit')

  return (
    <Form.Item
      shouldUpdate={(prevState, currentState) => {
        const prev = prevState?.fundingSources?.[name]
        const curr = currentState?.fundingSources?.[name]
        if (prev?.type !== curr?.type) {
          return true
        }
        const prevRates = prev?.chargeRates || []
        const currRates = curr?.chargeRates || []
        return prevRates.length !== currRates.length
      }}
    >
      {({ getFieldValue }) => {
        const currentChargeRates: VisitTypeValue[] =
          getFieldValue(['fundingSources', name, 'chargeRates']) || []
        const selectedIds = new Set(
          currentChargeRates
            .map((cr) => (cr as any)?.visitTypeId)
            .filter(Boolean)
        )
        const selectedNames = new Set(
          currentChargeRates
            .filter((cr) => !(cr as any)?.visitTypeId)
            .map((cr) => (cr as any)?.visitTypeName)
            .filter(Boolean)
        )
        const availableVisitTypeOptions = visitTypes
          .filter(
            (vt) =>
              !vt.isDeleted &&
              !selectedIds.has(vt.id) &&
              !selectedNames.has(vt.name)
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((vt) => ({
            value: { id: vt.id, name: vt.name },
            label: vt.name
          }))

        return (
          <FormCard
            level={2}
            title={
              getFundingSourceTitle(
                getFieldValue(['fundingSources', name, 'type'])
              ) || `Funding ${name + 1}`
            }
            rightChildren={
              <DeleteButton onClick={onDelete} testId={deleteButtonTestId}>
                {t('DELETE_FUNDING')}
              </DeleteButton>
            }
            testId={formCardTestId}
          >
            <FormItem
              name={[name, 'type']}
              label={<Text>{t('FUNDING_TYPE')}</Text>}
              rules={[getRequiredRule(t('FUNDING_TYPE'))]}
              data-testid={fundingTypeDropdownTestId}
            >
              <Select
                data-testid={`${fundingTypeDropdownTestId}-select`}
                options={getFilteredFundingTypeOptions(
                  fundingTypeOptions,
                  getFieldValue('fundingSources')
                )}
                onChange={() => {
                  form.setFieldValue(
                    ['fundingSources', name, 'chargeRates'],
                    []
                  )
                }}
                notFoundContent={
                  <EmptyState
                    title={
                      <EmptyStateWrapper>
                        <Text>{t('SELECT_EMPTY_STATE_FUNDING_1')}</Text>
                        <Text>{t('SELECT_EMPTY_STATE_FUNDING_2')}</Text>
                      </EmptyStateWrapper>
                    }
                    description={
                      <Link to={fundingUrl} target="_blank" rel="noreferrer">
                        <Button
                          variant="primary"
                          data-testid={editButtonTestId}
                        >
                          {t('EDIT')}
                        </Button>
                      </Link>
                    }
                    icon={
                      <NoDataIconWrapper>
                        <NoDataIcon />
                      </NoDataIconWrapper>
                    }
                    testId={emptyStateTestId}
                  />
                }
              />
            </FormItem>
            <FormItem
              name={[name, 'hoursPerWeek']}
              label={<Text>{t('HOURS_FUNDED')}</Text>}
              rules={[getRequiredRule(t('HOURS_FUNDED'))]}
            >
              <Input
                type="number"
                suffix={<InputSuffix>{t('HOURS_PER_WEEK')}</InputSuffix>}
                min={0}
                data-testid={fundedHoursInputTestId}
              />
            </FormItem>
            {showChargeRates && (
              <>
                <Divider style={{ margin: 0 }} />
                <ChargeRatesSection>
                  <ChargeRatesTitleRow>
                    <ChargeRatesSectionTitle level={5}>
                      {t('TYPE_OF_VISIT_RATES')}
                    </ChargeRatesSectionTitle>
                    <Tooltip
                      title={t('TYPE_OF_VISIT_RATES_TOOLTIP')}
                      overlayInnerStyle={{
                        backgroundColor: '#000',
                        borderRadius: '9px',
                        padding: '8px 12px'
                      }}
                      color="#000"
                    >
                      <QuestionMarkIconWrapper>
                        <QuestionMarkIcon />
                      </QuestionMarkIconWrapper>
                    </Tooltip>
                  </ChargeRatesTitleRow>
                  <Form.List name={[name, 'chargeRates']}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((crField) => {
                          const visitTypeName = getFieldValue([
                            'fundingSources',
                            name,
                            'chargeRates',
                            crField.name,
                            'visitTypeName'
                          ])
                          return (
                            <ChargeRateRow key={crField.key}>
                              <FormItem
                                name={[crField.name, 'amount']}
                                label={<Text>{visitTypeName}</Text>}
                                rules={
                                  isAdmin
                                    ? [
                                        {
                                          required: true,
                                          message: t('CHARGE_RATE_REQUIRED')
                                        }
                                      ]
                                    : []
                                }
                              >
                                <InputNumber
                                  min={0.01}
                                  max={9999.99}
                                  step={0.01}
                                  precision={2}
                                  placeholder="0.00"
                                  style={{ width: 260 }}
                                  disabled={!isAdmin}
                                  addonAfter={
                                    <FormItem
                                      name={[crField.name, 'unit']}
                                      noStyle
                                    >
                                      <RateTypeSelect
                                        disabled={!isAdmin}
                                        options={[
                                          {
                                            value: 'per_hour',
                                            label: t('RATE_TYPE_HOURLY')
                                          },
                                          {
                                            value: 'fixed',
                                            label: t('RATE_TYPE_FIXED')
                                          }
                                        ]}
                                      />
                                    </FormItem>
                                  }
                                />
                              </FormItem>
                              {isAdmin && (
                                <Button
                                  type="button"
                                  variant="text"
                                  icon={
                                    <Trash color={theme.color.neutral.color4} />
                                  }
                                  onClick={() => remove(crField.name)}
                                >
                                  <RemoveText>{t('REMOVE')}</RemoveText>
                                </Button>
                              )}
                            </ChargeRateRow>
                          )
                        })}
                        {isAdmin &&
                          (isAddingRate ? (
                            <AddRateDropdown>
                              <SelectAddOption<VisitTypeValue>
                                key={currentChargeRates.length}
                                options={availableVisitTypeOptions}
                                selectedValue={undefined}
                                setSelectedValue={(visitType) => {
                                  add({
                                    visitTypeId: visitType.id,
                                    visitTypeName: visitType.name,
                                    unit: 'per_hour'
                                  })
                                  setIsAddingRate(false)
                                }}
                                inputPlaceholder={t('TYPE_NEW_VISIT_TYPE')}
                                placeholder={t('ADD_CHARGE_RATE')}
                                valueKey="name"
                                defaultOpen
                                onDropdownClose={() => setIsAddingRate(false)}
                              />
                            </AddRateDropdown>
                          ) : (
                            <Button
                              type="button"
                              variant="default"
                              icon={<Add />}
                              onClick={() => setIsAddingRate(true)}
                            >
                              {t('ADD_CHARGE_RATE')}
                            </Button>
                          ))}
                      </>
                    )}
                  </Form.List>
                </ChargeRatesSection>
              </>
            )}
          </FormCard>
        )
      }}
    </Form.Item>
  )
}

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`

const ChargeRatesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
`

const ChargeRatesSectionTitle = styled(Heading)`
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ChargeRateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-form-item {
    flex: none;
  }
`

const RemoveText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const RateTypeSelect = styled(AntdSelect)`
  &.ant-select {
    width: 120px;
  }
`

const AddRateDropdown = styled.div`
  width: 300px;
`

const ChargeRatesTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  margin-top: -8px;
`

const QuestionMarkIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`
